/*
 * Asset Library's Brand Templates tab: a card grid, and a layout preview screen with multi-select insert.
 * The footer state is reported up to AssetLibraryContent (types.ts) so there is one footer.
 * The insert runs in the task pane (see useAssetLibraryLauncher.ts).
 * With `dialogTemplates` it mirrors the dialog's load state; otherwise it fetches directly.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ForwardedRef } from "react";
import { useSavedBrandTemplates } from "../../apply-brand/hooks/useSavedBrandTemplates";
import {
  insertBrandTemplateLayouts,
  logSavedBrandTemplatesError,
  type BrandTemplateSlide,
  type SavedBrandTemplate,
} from "../../apply-brand/services/brandTemplatesApi";
import {
  notifyPreviewedTemplateUnavailable, setPendingSavedTemplateSelection,
} from "../../apply-brand/services/pendingSavedTemplateSelection";
import { requestDeckorateView } from "../../../../../../integrations/tools/deckorate";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { DeckReadOnlyError, isDeckReadOnly } from "../../slide-templates/services/slideTemplateInsert";
import { useBrandThumbnails, type DialogBrandTemplates } from "../hooks/useDialogBrandTemplates";
import { useRequestNewSelection, type AssetLibraryFooterState } from "../types";
import {
  consumePendingBrandTemplatePreview,
  type BrandTemplatePreviewRequest,
} from "../services/brandTemplatePreviewHandoff";
import styles from "./AssetLibraryDialog.module.css";

/** What the shell can ask of the tab when a selection conflict is answered. */
export interface BrandTemplatesTabHandle {
  /** Discards every uninserted pick ("Clear and Continue" from another tab). */
  clearSelection: () => void;
  /** Opens the template the picks live in ("Keep My Picks" from another tab). */
  showSelection: () => void;
}

interface BrandTemplatesTabProps {
  readonly onClose: () => void;
  /** Supplied only by AssetLibraryDialogApp (the real Office Dialog window). */
  readonly dialogTemplates?: DialogBrandTemplates;
  /** Reports the current footer label/button up to AssetLibraryContent —
   *  null while browsing the grid (nothing open to act on yet). */
  readonly onFooterStateChange: (state: AssetLibraryFooterState | null) => void;
  /** Reports how many layouts are picked and not yet inserted (0 when none). */
  readonly onSelectionCountChange?: (count: number) => void;
}

interface TabViewProps {
  readonly onClose: () => void;
  readonly onFooterStateChange: (state: AssetLibraryFooterState | null) => void;
  readonly onSelectionCountChange: ((count: number) => void) | undefined;
  readonly controlRef: ForwardedRef<BrandTemplatesTabHandle>;
}

export const BrandTemplatesTab = forwardRef<BrandTemplatesTabHandle, BrandTemplatesTabProps>(function BrandTemplatesTab(
  { onClose, dialogTemplates, onFooterStateChange, onSelectionCountChange },
  ref,
) {
  if (dialogTemplates) {
    return (
      <DialogBrandTemplatesView
        dialogTemplates={dialogTemplates}
        onClose={onClose}
        onFooterStateChange={onFooterStateChange}
        onSelectionCountChange={onSelectionCountChange}
        controlRef={ref}
      />
    );
  }
  return (
    <InlineBrandTemplatesView
      onClose={onClose}
      onFooterStateChange={onFooterStateChange}
      onSelectionCountChange={onSelectionCountChange}
      controlRef={ref}
    />
  );
});

/** In-pane path: fetches directly (this pane has auth) and inserts directly
 *  (this pane can call PowerPoint.run()), then closes the (inline-only) overlay.
 *  Same realm as ApplyBrandPanel, so Use/Switch This Template (view-only
 *  Preview mode) parks the pick and navigates directly — no cross-window
 *  message needed, unlike the dialog path's switchTo(). */
function InlineBrandTemplatesView({
  onClose, onFooterStateChange, onSelectionCountChange, controlRef,
}: Readonly<TabViewProps>) {
  const { loadState, templates, retry } = useSavedBrandTemplates();

  const handleInsert = useCallback(
    async (template: SavedBrandTemplate, slideIndexes: readonly number[]) => {
      if (isDeckReadOnly()) throw new DeckReadOnlyError();
      const inserted = await insertBrandTemplateLayouts(template, slideIndexes);
      onClose();
      return inserted;
    },
    [onClose],
  );

  const handleUseTemplate = useCallback(
    (template: SavedBrandTemplate) => {
      setPendingSavedTemplateSelection({ kind: "org", template });
      requestDeckorateView("apply-brand");
      onClose();
    },
    [onClose],
  );

  return (
    <BrandTemplatesGridOrDetail
      status={loadState.status}
      templates={templates}
      onRetry={retry}
      onInsert={handleInsert}
      onUseTemplate={handleUseTemplate}
      onUnavailable={notifyPreviewedTemplateUnavailable}
      onClose={onClose}
      onFooterStateChange={onFooterStateChange}
      onSelectionCountChange={onSelectionCountChange}
      controlRef={controlRef}
    />
  );
}

/** Dialog path: mirrors useDialogBrandTemplates' load state and delegates the
 *  insert to it. Use/Switch This Template delegates to switchTo(), which
 *  messages the pane (serveSwitchBrandTemplate in useAssetLibraryLauncher.ts)
 *  to park the pick, navigate to Apply Brand, and close this window — this
 *  window never closes itself. */
function DialogBrandTemplatesView({
  dialogTemplates, onClose, onFooterStateChange, onSelectionCountChange, controlRef,
}: Readonly<TabViewProps & { dialogTemplates: DialogBrandTemplates }>) {
  const { state, request, retry, insert, switchTo, notifyUnavailable } = dialogTemplates;
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    request();
    // Runs once, when the tab first mounts — the load itself is lazy so
    // opening the Asset Library for slides never triggers an unused fetch.
  }, [request]);

  const handleInsert = useCallback(
    (template: SavedBrandTemplate, slideIndexes: readonly number[]) => insert(template.id, slideIndexes),
    [insert],
  );

  const handleUseTemplate = useCallback(
    (template: SavedBrandTemplate) => switchTo(template.id),
    [switchTo],
  );

  const status = state.status === "idle" ? "loading" : state.status;
  return (
    <BrandTemplatesGridOrDetail
      status={status}
      templates={state.status === "ready" ? state.org : []}
      onRetry={retry}
      onInsert={handleInsert}
      onUseTemplate={handleUseTemplate}
      onUnavailable={notifyUnavailable}
      onClose={onClose}
      onFooterStateChange={onFooterStateChange}
      onSelectionCountChange={onSelectionCountChange}
      controlRef={controlRef}
    />
  );
}

/** Fallback when a template has neither theme dark colours nor swatches —
 *  matches the neutral fallback Review & Customise's colour fields use. */
const HEADER_FALLBACK_COLOR = "334155";

const FOOTER_HINT = "Click a Brand template to preview the available slide layouts.";
const FOOTER_LAYOUTS_HINT = "Choose one or more layouts to add after your current slide.";
const FOOTER_SELECTION_NOTE = "they'll go after your current slide";
const INSERT_FAILED_MESSAGE = "Couldn't insert the selected layout(s).";
const FOOTER_LOAD_ERROR = "Couldn't reach the asset library";
const FOOTER_EMPTY = "0 brand templates";
const CLICK_TO_PREVIEW = "Click to preview";
const LOAD_ERROR_TITLE = "Couldn't load brand templates";
const LOAD_ERROR_MESSAGE = "Check your connection and try again.";
const EMPTY_TITLE = "No brand templates yet";
const EMPTY_MESSAGE =
  "When your organisation publishes brand templates, they'll appear here. Ask your brand administrator to add them.";
const PREVIEW_REMOVED_TITLE = "That brand template is no longer available.";
const PREVIEW_REMOVED_MESSAGE =
  "Your organisation updated the library, so nothing was changed. Choose another one instead.";
const ALREADY_APPLIED_LABEL = "Already applied to this presentation";

function headerColorFor(template: SavedBrandTemplate): string {
  return template.themeColors?.dk2 ?? template.themeColors?.dk1 ?? template.colors?.[0] ?? HEADER_FALLBACK_COLOR;
}

/** Alphabetical by name — case-insensitive, numbers in numeric order
 *  ("Deck 2" before "Deck 10") — with the id as a stable tie-break. */
function compareByName(a: SavedBrandTemplate, b: SavedBrandTemplate): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }) || a.id.localeCompare(b.id);
}

/** The view-only Preview footer's label + action, comparing the template
 *  currently open against the Apply Brand selection snapshot (BRD FR-20). */
function previewFooterFor(
  request: BrandTemplatePreviewRequest,
  focused: SavedBrandTemplate,
): { readonly label: string; readonly action: "Use This Template" | "Switch Template" | "Keep This Template" } {
  if (!request.selectedId) return { label: `${focused.name} will be selected in Apply Brand`, action: "Use This Template" };
  if (request.selectedId === focused.id) return { label: `${focused.name} is already selected in Apply Brand`, action: "Keep This Template" };
  return { label: `Switching from ${request.selectedName ?? "the current selection"} to ${focused.name}`, action: "Switch Template" };
}

/** The tab's three load states — org templates (and the dialog's mirror of
 *  them) are either still loading, failed, or in hand. */
type BrandTemplatesStatus = "loading" | "error" | "ready";

/** The guidance-note label shown while browsing the grid (nothing open) —
 *  shared by the view-only-preview-browsing and the ordinary default footer. */
function gridFooterLabel(status: BrandTemplatesStatus, hasNoTemplates: boolean): string {
  if (status === "error") return FOOTER_LOAD_ERROR;
  if (hasNoTemplates) return FOOTER_EMPTY;
  return FOOTER_HINT;
}

/** EC-08: the template this preview was showing was withdrawn while open.
 *  "Use This Template" stays labelled but disabled; Cancel (the shell's own
 *  persistent button) is the only way forward. */
function removedFooterState(): AssetLibraryFooterState {
  return { selectionLabel: PREVIEW_REMOVED_TITLE, actionLabel: "Use This Template", actionDisabled: true, onAction: () => undefined };
}

/** View-only Preview mode (FR-20/FR-21): no layout selection, just
 *  Use/Switch/Keep This Template. Cancel is the shell's own persistent
 *  button (AssetLibraryContent.tsx) — it already closes without acting. */
function previewDetailFooterState(
  request: BrandTemplatePreviewRequest,
  focused: SavedBrandTemplate,
  onClose: () => void,
  onUseTemplate: (template: SavedBrandTemplate) => void,
): AssetLibraryFooterState {
  const { label, action } = previewFooterFor(request, focused);
  return {
    selectionLabel: label,
    actionLabel: action,
    actionDisabled: false,
    onAction: action === "Keep This Template" ? onClose : () => onUseTemplate(focused),
  };
}

/** Browsing the grid mid-preview (FR-19) — nothing to confirm yet. */
function previewBrowsingFooterState(status: BrandTemplatesStatus, hasNoTemplates: boolean): AssetLibraryFooterState {
  return {
    selectionLabel: gridFooterLabel(status, hasNoTemplates),
    actionLabel: "Use This Template",
    actionDisabled: true,
    hideAction: true,
    onAction: () => undefined,
  };
}

function insertFooterState(
  selectedCount: number,
  isInserting: boolean,
  clearSelection: () => void,
  insertSelected: () => void,
): AssetLibraryFooterState {
  const hasSelection = selectedCount > 0;
  return {
    selectionLabel: hasSelection ? `${selectedCount} selected` : FOOTER_LAYOUTS_HINT,
    selectionNote: hasSelection ? FOOTER_SELECTION_NOTE : undefined,
    onClear: hasSelection && !isInserting ? clearSelection : undefined,
    actionLabel: "Insert into Deck",
    actionDisabled: !hasSelection || isInserting,
    onAction: () => insertSelected(),
  };
}

function defaultFooterState(status: BrandTemplatesStatus, hasNoTemplates: boolean): AssetLibraryFooterState {
  return { selectionLabel: gridFooterLabel(status, hasNoTemplates), actionLabel: "Open in Apply Brand", actionDisabled: true, hideAction: true, onAction: () => undefined };
}

/** Dispatches to the footer state for whichever mode the tab is in. Each
 *  branch's own logic lives in its own function above, so this reads as a
 *  priority list rather than one deeply nested effect body. */
function computeFooterState(params: {
  readonly previewRequest: BrandTemplatePreviewRequest | null;
  readonly isRemovedWhilePreviewing: boolean;
  readonly open: SavedBrandTemplate | null;
  readonly status: BrandTemplatesStatus;
  readonly hasNoTemplates: boolean;
  readonly selectedCount: number;
  readonly isInserting: boolean;
  readonly onClose: () => void;
  readonly onUseTemplate: (template: SavedBrandTemplate) => void;
  readonly clearSelection: () => void;
  readonly insertSelected: () => void;
}): AssetLibraryFooterState {
  const {
    previewRequest, isRemovedWhilePreviewing, open, status, hasNoTemplates, selectedCount, isInserting,
    onClose, onUseTemplate, clearSelection, insertSelected,
  } = params;
  if (previewRequest && isRemovedWhilePreviewing) return removedFooterState();
  if (previewRequest && open) return previewDetailFooterState(previewRequest, open, onClose, onUseTemplate);
  if (previewRequest) return previewBrowsingFooterState(status, hasNoTemplates);
  if (open) return insertFooterState(selectedCount, isInserting, clearSelection, insertSelected);
  return defaultFooterState(status, hasNoTemplates);
}

function BrandTemplatesGridOrDetail({
  status, templates, onRetry, onInsert, onUseTemplate, onUnavailable, onClose, onFooterStateChange, onSelectionCountChange, controlRef,
}: Readonly<{
  status: BrandTemplatesStatus;
  templates: readonly SavedBrandTemplate[];
  onRetry: () => void;
  /** Inserts the picked layouts (1-based slide numbers); resolves with how many went in. */
  onInsert: (template: SavedBrandTemplate, slideIndexes: readonly number[]) => Promise<number>;
  /** Use This Template / Switch Template, in view-only Preview mode (FR-21). */
  onUseTemplate: (template: SavedBrandTemplate) => void;
  /** The previewed template vanished from the library while still open (EC-08). */
  onUnavailable: (id: string) => void;
  onClose: () => void;
  onFooterStateChange: (state: AssetLibraryFooterState | null) => void;
  onSelectionCountChange: ((count: number) => void) | undefined;
  controlRef: ForwardedRef<BrandTemplatesTabHandle>;
}>) {
  const requestNewSelection = useRequestNewSelection("brand-templates");
  const [openId, setOpenId] = useState<string | null>(null);
  // Picks held so far and their template: one template at a time, kept while browsing elsewhere.
  const [selection, setSelection] = useState<{ readonly templateId: string; readonly indexes: readonly number[] } | null>(null);
  const [isInserting, setIsInserting] = useState(false);
  const [insertError, setInsertError] = useState<string | null>(null);
  // Set once, from Apply Brand's eye icon (see brandTemplatePreviewHandoff.ts),
  // and kept for the rest of this open: every template viewed afterwards (FR-19
  // — browsing changes the preview only) stays in this view-only footer mode,
  // never the normal Insert into Deck one.
  const [previewRequest, setPreviewRequest] = useState<BrandTemplatePreviewRequest | null>(null);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const open = status === "ready" && openId ? templates.find((t) => t.id === openId) ?? null : null;
  const appliedPendingPreviewRef = useRef(false);
  const sortedTemplates = useMemo(() => [...templates].sort(compareByName), [templates]);
  const thumbnails = useBrandThumbnails(templates.map((t) => t.thumbnailCacheUrl));

  // Apply Brand's own Templates tab can route straight in here for a specific
  // template (its eye icon — see brandTemplatePreviewHandoff.ts). Applies
  // once, as soon as the real list is ready, so this open lands on that
  // template's detail instead of the grid, in view-only mode. A template id
  // that's already gone by the time Preview opens just lands on the grid
  // (quietly — the organisation's own withdrawn-template messaging covers
  // that at apply time); `isRemovedWhilePreviewing` below covers the template
  // vanishing while the preview is actively open on it (EC-08).
  useEffect(() => {
    if (appliedPendingPreviewRef.current || status !== "ready") return;
    appliedPendingPreviewRef.current = true;
    const pending = consumePendingBrandTemplatePreview();
    if (!pending) return;
    setPreviewRequest(pending);
    if (templates.some((t) => t.id === pending.id)) setOpenId(pending.id);
  }, [status, templates]);

  // The template the preview was showing has disappeared from the library
  // while still open (EC-08) — tell Apply Brand's pane once, so it's already
  // in sync by the time this window closes.
  const isRemovedWhilePreviewing = previewRequest !== null && openId !== null && status === "ready" && !open;
  const notifiedUnavailableRef = useRef(false);
  useEffect(() => {
    if (!isRemovedWhilePreviewing || notifiedUnavailableRef.current || !openId) return;
    notifiedUnavailableRef.current = true;
    onUnavailable(openId);
  }, [isRemovedWhilePreviewing, openId, onUnavailable]);

  const selectedIndexes = useMemo(
    () => (open && selection?.templateId === open.id ? selection.indexes : []),
    [open, selection],
  );

  const toggleLayout = useCallback(
    (slideIndex: number) => {
      if (!open || isInserting) return;
      setInsertError(null);
      const owned = selection?.templateId === open.id ? selection : null;

      // Deselecting a pick is always free.
      if (owned?.indexes.includes(slideIndex)) {
        const indexes = owned.indexes.filter((index) => index !== slideIndex);
        setSelection(indexes.length > 0 ? { templateId: open.id, indexes } : null);
        return;
      }

      // Picks held in another template: the shell asks first (Keep returns there; Clear starts fresh here).
      const heldElsewhere = selection && !owned ? selection : null;
      requestNewSelection(
        () =>
          setSelection((prev) =>
            prev?.templateId === open.id
              ? { templateId: open.id, indexes: [...prev.indexes, slideIndex] }
              : { templateId: open.id, indexes: [slideIndex] },
          ),
        heldElsewhere ? { conflictsWithOwnPicks: true } : undefined,
      );
    },
    [open, isInserting, selection, requestNewSelection],
  );

  const clearSelection = useCallback(() => {
    setInsertError(null);
    setSelection(null);
  }, []);

  useImperativeHandle(
    controlRef,
    () => ({
      clearSelection,
      showSelection: () => {
        if (selection) setOpenId(selection.templateId);
      },
    }),
    [clearSelection, selection],
  );

  const totalSelected = selection?.indexes.length ?? 0;
  useEffect(() => {
    onSelectionCountChange?.(totalSelected);
  }, [totalSelected, onSelectionCountChange]);

  const insertSelected = useCallback(async () => {
    if (!open || selectedIndexes.length === 0 || isInserting) return;
    setIsInserting(true);
    setInsertError(null);
    try {
      await onInsert(open, selectedIndexes);
      if (isMountedRef.current) setSelection(null);
    } catch (error) {
      const message = getErrorMessage(error, INSERT_FAILED_MESSAGE) || INSERT_FAILED_MESSAGE;
      logSavedBrandTemplatesError("brand-templates-tab-insert", message);
      if (isMountedRef.current) setInsertError(message);
    } finally {
      if (isMountedRef.current) setIsInserting(false);
    }
  }, [open, selectedIndexes, isInserting, onInsert]);

  // Reports this tab's footer state up; cleared on unmount so no stale button lingers.
  const hasNoTemplates = status === "ready" && templates.length === 0;
  const selectedCount = selectedIndexes.length;
  useEffect(() => {
    const state = computeFooterState({
      previewRequest, isRemovedWhilePreviewing, open, status, hasNoTemplates, selectedCount, isInserting,
      onClose, onUseTemplate, clearSelection, insertSelected,
    });
    onFooterStateChange(state);
    return () => onFooterStateChange(null);
  }, [
    open, status, hasNoTemplates, selectedCount, isInserting, clearSelection, insertSelected, onClose, onFooterStateChange,
    previewRequest, onUseTemplate, isRemovedWhilePreviewing,
  ]);

  if (status === "loading") {
    return (
      <div className={styles.centered}>
        <span className={styles.spinner} aria-hidden="true" />
        <span>Loading brand templates…</span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className={styles.centered}>
        <span className={styles.stateTitle}>{LOAD_ERROR_TITLE}</span>
        <span className={styles.stateMessage}>{LOAD_ERROR_MESSAGE}</span>
        <button type="button" className={styles.retryBtn} onClick={onRetry}>Try Again</button>
      </div>
    );
  }

  if (isRemovedWhilePreviewing) {
    return (
      <div className={styles.centered}>
        <span className={styles.stateTitle}>{PREVIEW_REMOVED_TITLE}</span>
        <span className={styles.stateMessage}>{PREVIEW_REMOVED_MESSAGE}</span>
      </div>
    );
  }

  if (open) {
    return (
      <BrandTemplateDetail
        template={open}
        thumbnails={thumbnails}
        selected={selectedIndexes}
        insertError={insertError}
        onToggle={toggleLayout}
        onBack={() => setOpenId(null)}
        isViewOnly={previewRequest !== null}
        isAlreadyApplied={previewRequest !== null && previewRequest.appliedId === open.id}
      />
    );
  }

  if (hasNoTemplates) {
    return (
      <div className={styles.centered}>
        <span className={styles.stateTitle}>{EMPTY_TITLE}</span>
        <span className={styles.stateMessage}>{EMPTY_MESSAGE}</span>
      </div>
    );
  }

  return (
    <div className={styles.brandTemplatesScroll}>
      <ul className={styles.brandTemplatesGrid}>
        {sortedTemplates.map((template) => (
          <li key={template.id}>
            <button type="button" className={styles.brandTemplateCard} onClick={() => setOpenId(template.id)}>
              <SlideThumb
                template={template}
                src={thumbnailSrc(template.slides?.[0], thumbnails)}
              />
              <span className={styles.brandTemplateCardFooter}>
                <span className={styles.brandTemplateCardName} title={template.name}>{template.name}</span>
                <span className={styles.brandTemplateCardMeta}>
                  {template.slideSize ? `${template.slideSize} · ${CLICK_TO_PREVIEW}` : CLICK_TO_PREVIEW}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function thumbnailSrc(slide: BrandTemplateSlide | undefined, thumbnails: ReadonlyMap<string, string>): string | null {
  return slide?.thumbnailKey ? thumbnails.get(slide.thumbnailKey) ?? null : null;
}

/** A slide's thumbnail, or a brand-tinted placeholder; both fill the same fixed-ratio box. */
function SlideThumb({ template, src }: Readonly<{ template: SavedBrandTemplate; src: string | null }>) {
  return (
    <span className={styles.brandTemplateThumb}>
      {src ? (
        <img className={styles.brandTemplateThumbImg} src={src} alt="" />
      ) : (
        <span className={styles.brandTemplateThumbFallback} aria-hidden="true">
          <span className={styles.brandTemplateCardHeader} style={{ backgroundColor: `#${headerColorFor(template)}` }}>
            Aa
          </span>
          <span className={styles.brandTemplateFallbackBody}>
            <span className={styles.brandTemplateCardSwatches}>
              {(template.colors ?? []).slice(0, 6).map((hex) => (
                <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} />
              ))}
            </span>
            <span className={styles.brandTemplateFallbackBar} />
          </span>
        </span>
      )}
    </span>
  );
}

/** The layout preview. Normally each layout toggles on click for Insert into
 *  Deck (in the shared footer); in view-only Preview mode (`isViewOnly`, BRD
 *  FR-18) layouts are read-only thumbnails with no selection at all, and an
 *  "Already applied" badge can show instead. */
function BrandTemplateDetail({ template, thumbnails, selected, insertError, onToggle, onBack, isViewOnly = false, isAlreadyApplied = false }: Readonly<{
  template: SavedBrandTemplate;
  thumbnails: ReadonlyMap<string, string>;
  /** 1-based slide numbers currently selected. */
  selected: readonly number[];
  insertError: string | null;
  onToggle: (slideIndex: number) => void;
  onBack: () => void;
  isViewOnly?: boolean;
  isAlreadyApplied?: boolean;
}>) {
  const slides = template.slides ?? [];
  return (
    <div className={styles.brandTemplateDetail}>
      <div className={styles.brandTemplateDetailHeaderRow}>
        <button type="button" className={styles.brandTemplateBackBtn} onClick={onBack}>
          <svg aria-hidden="true" viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="12,4 6,10 12,16" />
          </svg>
          Back
        </button>
        <h3 className={styles.brandTemplateDetailTitle}>
          <span className={styles.brandTemplateDetailName} title={template.name}>{template.name}</span>
          {slides.length > 0 && (
            <span className={styles.brandTemplateDetailCount}>
              {` · ${slides.length} ${slides.length === 1 ? "layout" : "layouts"}`}
            </span>
          )}
        </h3>
      </div>

      {isViewOnly && isAlreadyApplied && (
        <output className={styles.brandAlreadyApplied}>{ALREADY_APPLIED_LABEL}</output>
      )}

      {insertError && (
        <div className={styles.brandInsertError} role="alert">
          {insertError}
        </div>
      )}

      {slides.length === 0 ? (
        <p className={styles.brandTemplateNoLayouts}>This template has no slide layouts to preview.</p>
      ) : (
        <ul className={styles.brandLayoutGrid}>
          {slides.map((slide) => {
            const isSelected = !isViewOnly && selected.includes(slide.index);
            const thumb = (
              <span className={styles.brandLayoutThumbWrap}>
                <SlideThumb template={template} src={thumbnailSrc(slide, thumbnails)} />
                {!isViewOnly && (
                  <span className={isSelected ? styles.brandLayoutCheck : styles.brandLayoutUncheck} aria-hidden="true">
                    {isSelected ? "✓" : ""}
                  </span>
                )}
              </span>
            );
            return (
              <li key={slide.index}>
                {isViewOnly ? (
                  <div className={styles.brandLayoutCard}>
                    {thumb}
                    <span className={styles.brandLayoutName} title={slide.name}>{slide.name}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={isSelected ? `${styles.brandLayoutCard} ${styles.brandLayoutCardSelected}` : styles.brandLayoutCard}
                    aria-pressed={isSelected}
                    onClick={() => onToggle(slide.index)}
                  >
                    {thumb}
                    <span className={styles.brandLayoutName} title={slide.name}>{slide.name}</span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
